'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef, type MouseEvent } from 'react'
import { Box } from '@mantine/core'
import InsurancePill from '../InsuranceWidget/InsurancePill'
import classes from './SearchHeader.module.css'
import { wordAnimationVariants, transitionBase, headerFoucVariants } from '@/lib/animations'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { usePrescriptionRenewalStorefrontNavigation } from '@/hooks/usePrescriptionRenewalStorefrontNavigation'
import { MobileSearchInput } from '@/components/MobileSearchInput'
import { PRESCRIPTION_RENEWAL_HREF } from '@/types/enums/sku-constants'

export interface SearchHeaderProps {
    handleSearchClick: () => void
    loggedIn: boolean
}

const SKU_LINKS = [
    {
        text: 'specialty referral',
        href: '/store/specialty-care',
    },
    {
        text: 'GLP-1 consult',
        href: '/s/glp1-medication-evaluation/300543',
    },
    {
        text: 'Rx renewal',
        href: PRESCRIPTION_RENEWAL_HREF,
    },
    {
        text: 'skin consult',
        href: '/s/dermatology-consult/100014',
    },
    {
        text: 'wellness test',
        href: '/s/annual-wellness-test/500001',
    },
    {
        text: 'urgent care',
        href: '/s/urgent-care/400002',
    },
]

export default function SearchHeader({ handleSearchClick, loggedIn }: SearchHeaderProps) {
    const { isSm } = useBreakpoint()
    const [currentIndex, setCurrentIndex] = useState(0)
    const sectionRef = useRef<HTMLDivElement>(null)
    const { href: prescriptionRenewalHref, isNavigating, navigateToPrescriptionRenewal } =
        usePrescriptionRenewalStorefrontNavigation(loggedIn)

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % SKU_LINKS.length)
        }, 5000)

        return () => clearInterval(interval)
    }, [])

    const currentLink = SKU_LINKS[currentIndex]
    const isPrescriptionRenewalHeadline = currentLink.href === PRESCRIPTION_RENEWAL_HREF
    const headlineHref = isPrescriptionRenewalHeadline ? prescriptionRenewalHref : currentLink.href

    return (
        <Box ref={sectionRef} className={`${classes.container} ${isSm ? classes.mobileContainer : ''}`}>
            {!loggedIn && (
                <motion.div variants={headerFoucVariants} initial="hidden" animate="visible">
                    <InsurancePill />
                </motion.div>
            )}

            <Box
                component="h1"
                className={`${classes.headlineContainer} ${isSm ? classes.mobileHeadlineContainer : ''}`}
            >
                <motion.span
                    className={`${classes.headlineRow} ${isSm ? classes.mobileHeadlineRow : ''}`}
                    variants={headerFoucVariants}
                    initial="hidden"
                    animate="visible"
                >
                    Instantly book your
                </motion.span>

                <motion.a
                    href={headlineHref}
                    className={classes.headlineLink}
                    onClick={
                        loggedIn && isPrescriptionRenewalHeadline
                            ? (event: MouseEvent<HTMLAnchorElement>) => {
                                  void navigateToPrescriptionRenewal(event)
                              }
                            : undefined
                    }
                    aria-busy={isPrescriptionRenewalHeadline && isNavigating ? true : undefined}
                    style={
                        isPrescriptionRenewalHeadline && isNavigating ? { cursor: 'wait' } : undefined
                    }
                    whileHover={{
                        scale: 1.02,
                        transition: {
                            ...transitionBase,
                            duration: 0.3,
                        },
                    }}
                >
                    <AnimatePresence mode="wait">
                        <motion.span
                            key={currentIndex}
                            className={`${classes.headlineText} ${isSm ? classes.mobileHeadlineText : ''}`}
                        >
                            <SplitText text={SKU_LINKS[currentIndex].text} textKey={currentIndex} isSm={!!isSm} />
                        </motion.span>
                    </AnimatePresence>
                </motion.a>
            </Box>

            <motion.div
                variants={headerFoucVariants}
                initial="hidden"
                animate="visible"
                onClick={!isSm ? handleSearchClick : undefined}
                className={`${classes.searchBar} ${isSm ? classes.mobileSearchBar : ''}`}
            >
                {isSm && (
                    <MobileSearchInput
                        onActivate={handleSearchClick}
                        aria-label="Search"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            color: 'transparent',
                            caretColor: 'transparent',
                            cursor: 'pointer',
                        }}
                    />
                )}
                <Box className={classes.searchIconWrapper}>
                    <SearchIcon />
                </Box>
                <Box className={classes.searchText}>
                    {isSm
                        ? 'Search specialties, labs, concerns...'
                        : 'Search conditions, specialties, labs, prescriptions...'}
                </Box>
            </motion.div>
        </Box>
    )
}

const SplitText = ({ text, textKey, isSm }: { text: string; textKey: string | number; isSm: boolean }) => {
    const words = text.split(' ')

    return (
        <>
            {words.map((word, wordIndex) => (
                <motion.span
                    key={`${textKey}-${wordIndex}`}
                    custom={{ i: wordIndex, totalWords: words.length + 1 }}
                    variants={wordAnimationVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className={`${classes.word} ${wordIndex < words.length - 1 ? classes.wordWithMargin : ''} ${isSm ? classes.mobileHeadlineText : ''}`}
                >
                    {word}
                </motion.span>
            ))}
            <motion.span
                key={`${textKey}-arrow`}
                custom={{ i: words.length, totalWords: words.length + 1 }}
                variants={wordAnimationVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={`${classes.arrowIcon} ${isSm ? classes.mobileArrowIcon : ''}`}
            >
                <ArrowIcon />
            </motion.span>
        </>
    )
}

const ArrowIcon = () => (
    <svg
        width="38"
        height="42"
        viewBox="0 0 38 42"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={classes.arrowIcon}
    >
        <rect x="0.5" y="0.5" width="36.9022" height="36.9476" rx="7.5" stroke="#2D1AFF" />
        <path
            d="M10.681 29.9476L8 27.2666L23.7678 11.4535L11.8624 11.4989V8H29.9022V25.9943H26.4487V14.2253L10.681 29.9476Z"
            fill="#2D1AFF"
        />
    </svg>
)

const SearchIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="11" cy="11" r="7" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m20 20-4.35-4.35" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)
